import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Avatar,
    Fade
} from '@mui/material';
import {
    SmartToy as BotIcon
} from '@mui/icons-material';
import DataVisualization from './DataVisualization';
import MultiStepProgress from './MultiStepProgress';
import StepResultHeader from './StepResultHeader';

// Helper function to parse markdown bold syntax and return React elements
const parseMarkdownBold = (text) => {
    if (!text) return text;

    // First, split by ". -" pattern to create line breaks
    const lines = text.split(/\.\s*-\s*/);

    const processLine = (line, lineIndex) => {
        const parts = [];
        let lastIndex = 0;
        const boldRegex = /\*\*(.*?)\*\*/g;
        let match;

        while ((match = boldRegex.exec(line)) !== null) {
            // Add text before the bold part
            if (match.index > lastIndex) {
                parts.push(line.substring(lastIndex, match.index));
            }

            // Add the bold part
            parts.push(
                <strong key={`${lineIndex}-${match.index}`} style={{ fontWeight: 700 }}>
                    {match[1]}
                </strong>
            );

            lastIndex = match.index + match[0].length;
        }

        // Add remaining text
        if (lastIndex < line.length) {
            parts.push(line.substring(lastIndex));
        }

        return parts.length > 0 ? parts : line;
    };

    // Process each line and add line breaks between them
    const result = [];
    lines.forEach((line, index) => {
        if (index > 0) {
            // Add period back and line break for all but the first line
            result.push('.');
            result.push(<br key={`br-${index}`} />);
            result.push('- ');
        }
        result.push(...(Array.isArray(processLine(line, index)) ? processLine(line, index) : [processLine(line, index)]));
    });

    return result.length > 0 ? result : text;
};

// Helper function to get segmentation image link
const getImageLink = (age, income) => {
    let incomeValue = 1;
    if (age !== 'under_18') {
        const incomeMapping = {
            'low_income': 1,
            'lower_middle_income': 2,
            'middle_income': 3,
            'upper_middle_income': 4,
            'high_income': 5,
            'very_high_income': 6
        };
        incomeValue = incomeMapping[income] || 1;
    }
    return `/segmentation/${age}_${incomeValue}.jpeg`;
};

const ChatMessage = ({
    message,
    index,
    messages,
    isFullscreen,
    multiStepData,
    formatTimestamp,
    onSaveToMain
}) => {
    const isUser = message.sender === 'user';
    const showAvatar = !isUser && (index === 0 || messages[index - 1]?.sender !== 'bot');
    
    return (
        <Fade in={true} key={message.id} timeout={300}>
            <Box
                sx={{
                    display: 'flex',
                    mb: 2,
                    justifyContent: isUser ? 'flex-end' : 'flex-start',
                    px: isFullscreen ? 3 : 2
                }}
            >
                {/* Bot avatar */}
                {!isUser && showAvatar && (
                    <Avatar 
                        sx={{ 
                            width: 32, 
                            height: 32, 
                            mr: 2,
                            bgcolor: 'primary.main',
                            alignSelf: 'flex-end'
                        }}
                    >
                        <BotIcon fontSize="small" />
                    </Avatar>
                )}
                
                {/* Spacer for non-avatar messages */}
                {!isUser && !showAvatar && <Box sx={{ width: 48 }} />}
                
                {/* Message content */}
                <Box
                    sx={{
                        maxWidth: isFullscreen ? '70%' : '85%',
                        minWidth: 120,
                        position: 'relative'
                    }}
                >
                    {/* Message bubble */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            bgcolor: isUser ? 'primary.main' : 'grey.100',
                            color: isUser ? 'primary.contrastText' : 'text.primary',
                            borderRadius: 4,
                            borderTopLeftRadius: !isUser && showAvatar ? 1 : 4,
                            borderTopRightRadius: isUser ? 1 : 4,
                            borderBottomLeftRadius: 4,
                            borderBottomRightRadius: 4,
                            position: 'relative',
                            wordBreak: 'break-word'
                        }}
                    >
                        {/* Multi-step step result header */}
                        {message.metadata?.type === 'step_result' && (
                            <StepResultHeader metadata={message.metadata} />
                        )}
                        
                        {/* Multi-step Progress Display */}
                        {(message.metadata?.type === 'multi_step_plan' && message.metadata?.plan) ? (
                            <Box sx={{ mt: 2 }}>
                                <MultiStepProgress 
                                    data={{
                                        goal: message.metadata.plan.goal,
                                        steps: message.metadata.plan.steps,
                                        stepResults: multiStepData?.stepResults,
                                        status: multiStepData?.status
                                    }} 
                                />
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                {/* Segmentation Image */}
                                {message.metadata?.segmentation && message.metadata.segmentation.age && message.metadata.segmentation.income && (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        minWidth: 100,
                                        maxWidth: 100
                                    }}>
                                        <Box
                                            component="img"
                                            src={getImageLink(message.metadata.segmentation.age, message.metadata.segmentation.income)}
                                            alt={`${message.metadata.segmentation.age} ${message.metadata.segmentation.income}`}
                                            sx={{
                                                width: '100%',
                                                height: 'auto',
                                                borderRadius: 1,
                                                border: '1px solid #e0e0e0'
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    </Box>
                                )}

                                {/* Message text */}
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: '0.95rem',
                                            lineHeight: 1.4,
                                            whiteSpace: 'pre-wrap',
                                            wordWrap: 'break-word',
                                            color: isUser ? 'white' : 'inherit'
                                        }}
                                    >
                                        {parseMarkdownBold(message.text)}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        
                        {/* Visualization */}
                        {message.visualizationData && (
                            <Box sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}>
                                <DataVisualization 
                                    visualizationData={message.visualizationData}
                                    onSaveToMain={onSaveToMain}
                                />
                            </Box>
                        )}
                        
                        {/* Timestamp */}
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                display: 'block', 
                                mt: 1, 
                                opacity: 0.7,
                                fontSize: '0.75rem'
                            }}
                        >
                            {formatTimestamp(message.timestamp)}
                        </Typography>
                    </Paper>
                </Box>
                
                {/* User avatar space (invisible) */}
                {isUser && <Box sx={{ width: 48 }} />}
            </Box>
            
        </Fade>
    );
};

export default ChatMessage;